import { headerUtilitySlotAtom } from "@/components/layout/HeaderUtilitySlot.tsx";
import { queryDevtoolsOpenAtom } from "@/components/layout/queryDevtoolsAtom.ts";
import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { useMarkerSdk } from "@/components/marker.io/MarkerIoContext.tsx";
import { MarkerIoReport } from "@/components/marker.io/MarkerIoReport.tsx";
import { Button } from "@/components/ui/button.tsx";
import { SHOW_QUERY_DEVTOOLS } from "@/lib/devFlags.ts";
import { useAtomValue, useSetAtom } from "jotai";
import { Database } from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Shows the React Query devtools panel, and hides it again when it is up.
 *
 * A button of our own rather than tanstack's floating toggle, which is the
 * corner control this change exists to get rid of. It carries a generic mark
 * rather than tanstack's logo: that logo is 15KB of SVG built on 39 gradient
 * IDs, which is not a price worth paying for a control only developers see.
 *
 * The panel itself is mounted at the app root — see {@link QueryDevtools} for
 * why it cannot live here beside the button.
 */
const DevtoolsButton = () => {
  const setOpen = useSetAtom(queryDevtoolsOpenAtom);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={panelHeaderControlClass}
      onClick={() => setOpen((open) => !open)}
      aria-label="Toggle React Query devtools"
      data-statsig-name="devtools-query"
    >
      <Database />
    </Button>
  );
};

/**
 * The controls, built once at module scope.
 *
 * Nothing here closes over state — `SHOW_QUERY_DEVTOOLS` is a build constant
 * and neither control takes a prop — so rebuilding the fragment per render
 * would only obscure that the subtree's identity never changes, which is what
 * lets it survive being moved between headers.
 *
 * The devtools gate is here rather than inside the button, so a production
 * build drops the component and its import of the devtools atom with it.
 */
const CONTROLS = (
  <>
    {SHOW_QUERY_DEVTOOLS && <DevtoolsButton />}
    <MarkerIoReport />
  </>
);

/**
 * The row the controls sit in, which travels with them.
 *
 * It belongs on the node being moved rather than on the slot, because the slot
 * is not the controls' parent any more — this node is. Left off, the controls
 * are block children of a bare div with no gap and no shared centre line, so a
 * filled disc and an outline glyph settle at different heights in a row whose
 * other controls are level.
 */
const HOSTED_CLASS = "flex items-center gap-0.5";

/** The same row, holding its own corner on a screen that publishes no slot. */
const FALLBACK_CLASS = `fixed top-3 right-4 z-9999 md:top-3.5 ${HOSTED_CLASS}`;

/**
 * The controls that ride the topmost header's control group: the devtools
 * toggle, on a development build, and the bug-report button after it.
 *
 * Mounted once per screen and portalled, rather than rendered by each header,
 * so that exactly one set exists however many headers are on screen — and so
 * the bug button's capture listener is installed once. Which header receives
 * them is `headerUtilitySlotAtom`'s business.
 *
 * The portal target is one element this owns for its lifetime, re-parented as
 * the slot changes, rather than the slot itself. React treats a portal given a
 * different container as a different tree and rebuilds everything inside it, so
 * portalling straight into the slot tore the controls down and built them again
 * on every screen — the ref that publishes a slot fires after the first render,
 * so even a plain navigation swapped the container once — and again whenever a
 * sidebar or a card opened. Moving one node instead leaves the controls mounted
 * and their listener registered, which is what the paragraph above claims.
 */
export const HeaderUtilities = () => {
  const slot = useAtomValue(headerUtilitySlotAtom);
  const { markerSdk } = useMarkerSdk();
  const [host] = useState(() => document.createElement("div"));

  // Whether anything would render, asked here as well as inside each control.
  // The host is a child of the slot, so appending it with nothing inside stops
  // the slot's own `empty:hidden` matching, and the header row keeps a gap for
  // a control that is not there.
  const hasControls = SHOW_QUERY_DEVTOOLS || !!markerSdk;

  useLayoutEffect(() => {
    if (!hasControls) return;
    host.className = slot ? HOSTED_CLASS : FALLBACK_CLASS;
    (slot ?? document.body).appendChild(host);
    return () => host.remove();
  }, [slot, host, hasControls]);

  return createPortal(CONTROLS, host);
};
